/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FunctionComponent, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import { Bem } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { Icon, ICON_SIZE } from "../Icon";
import { FC_ANCHOR, FC_DIRECTION, FC_SIZE } from "../FloatingContainer";
import { DROPDOWN_STYLE, DropdownMenu, IDropdownMenuItem } from "../DropdownMenu";
import { Spinner, SPINNER_SIZE } from "../Spinner";
import { Tooltip } from "../Tooltip";
import { useForkRef } from "@adapptio/lib-editor-ui";

export interface IButtonPropsBase {
	children?: React.ReactNode;
	label?: string;
	icon?: string;
	tooltip?: string;
	isLoading?: boolean;
	showLoading?: boolean;
	disabled?: boolean;
	active?: boolean;
	type?: "submit" | "button";
	menuItems?: IDropdownMenuItem[];
	selectedMenuItemId?: string;
	menuAnchor?: FC_ANCHOR;
	menuDirection?: FC_DIRECTION;
	menuSize?: FC_SIZE;
	menuStyle?: DROPDOWN_STYLE;
	tooltipAnchor?: FC_ANCHOR;
	hideMenuArrow?: boolean;
	cssStyle?: React.CSSProperties;
}

/**
 * Button props
 */
export interface IButtonProps extends IButtonPropsBase {
	className?: string;
	mark?: string;
	onClick?: () => void;
	onMenuItemClick?: (item: IDropdownMenuItem) => void;
	onMenuOpenedChange?: (isMenuOpened: boolean) => void;
}

export interface IButtonComponentProps extends IButtonProps {
	baseClass: Bem;
}

/**
 * Generic Button component
 */
export const Button = React.forwardRef<HTMLDivElement, IButtonComponentProps>(
	(
		{
			label,
			icon,
			tooltip,
			isLoading,
			showLoading,
			disabled,
			active,
			menuItems,
			selectedMenuItemId,
			onClick,
			onMenuItemClick,
			onMenuOpenedChange,
			menuAnchor,
			menuDirection,
			menuSize,
			menuStyle,
			tooltipAnchor,
			baseClass: _baseClass,
			className,
			mark,
			type,
			children,
			hideMenuArrow,
			cssStyle
		},
		ref
	) => {
		const rootElRef = useRef<HTMLDivElement>();
		const forkRootRef = useForkRef(rootElRef, ref);
		const primaryElRef = useRef<HTMLButtonElement>();
		const [ isMenuOpened, setIsMenuOpened ] = useState(false);

		const onPrimaryClick = useCallback(
			(ev: MouseEvent) => {
				ev.stopPropagation();

				if (disabled) {
					return;
				}

				if (onClick) {
					onClick();
				} else {
					setIsMenuOpened((s) => !s);
				}
			},
			[ onClick, disabled ]
		);

		const onToggleClick = useCallback(
			(ev: MouseEvent) => {
				ev.stopPropagation();

				if (disabled) {
					return;
				}

				setIsMenuOpened((s) => !s);
			},
			[ disabled ]
		);

		const handleMenuClose = useCallback(() => {
			setIsMenuOpened(false);
		}, []);

		const handleMenuItemClick = useCallback(
			(item: IDropdownMenuItem) => {
				if (disabled) {
					return;
				}

				if (onMenuItemClick) {
					onMenuItemClick(item);
				}

				setIsMenuOpened(false);
			},
			[ onMenuItemClick, disabled ]
		);

		useEffect(() => {
			if (onMenuOpenedChange) {
				onMenuOpenedChange(isMenuOpened);
			}
		}, [ onMenuOpenedChange, isMenuOpened ]);

		const baseClass = Bem.clone(_baseClass);

		baseClass.mod({
			disabled,
			"is-loading": (isLoading || false) && showLoading !== false,
			"with-primary-action": isFunction(onClick),
			"with-label": !!label,
			"with-icon": !!icon,
			"with-menu": !hideMenuArrow && menuItems?.length > 0,
			"is-opened": isMenuOpened,
			"is-active": active
		});

		if (className) {
			baseClass.add(className);
		}

		if (mark) {
			baseClass.add("m-" + mark);
		}

		return (
			<div
				className={baseClass.toClassName()}
				onClick={onPrimaryClick}
				ref={forkRootRef}
				style={cssStyle}
			>
				<button
					className={baseClass.element("content").toClassName()}
					tabIndex={disabled ? -1 : 0}
					disabled={disabled}
					ref={primaryElRef}
					type={type}
				>
					<div className={baseClass.element("icon-wrapper").toClassName()}>
						{icon && <Icon name={icon} size={ICON_SIZE.CUSTOM} />}

						<Transition in={isLoading && showLoading !== false} timeout={120} unmountOnExit>
							{(transitionState) => (
								<Spinner
									size={SPINNER_SIZE.CUSTOM}
									className={`edt-spinner--${transitionState}`}
								/>
							)}
						</Transition>
					</div>
					{label || children ? (
						<span className={baseClass.element("label").toClassName()}>{label || children}</span>
					) : null}
					<div className={baseClass.element("bg").toClassName()} />
					{tooltip ? (
						<Tooltip
							for={primaryElRef}
							anchor={tooltipAnchor || FC_ANCHOR.CENTER_TOP}
							openDelay={1000}
						>
							{tooltip}
						</Tooltip>
					) : null}
				</button>
				{menuItems && menuItems.length > 0 ? (
					<>
						{!hideMenuArrow && (
							<button
								className={baseClass.element("toggle").toClassName()}
								disabled={disabled}
								type="button"
								onClick={onToggleClick}
							>
								<Icon name="mdi/menu-down" size={ICON_SIZE.XSMALL} showLoader={false} />
							</button>
						)}
						<DropdownMenu
							parent={rootElRef}
							anchor={menuAnchor || FC_ANCHOR.RIGHT_BOTTOM}
							isOpened={isMenuOpened}
							direction={menuDirection || FC_DIRECTION.HR}
							size={menuSize || FC_SIZE.FIT_MIN}
							style={menuStyle || DROPDOWN_STYLE.DEFAULT}
							items={menuItems}
							selectedItemId={selectedMenuItemId}
							onItemClick={handleMenuItemClick}
							onClose={handleMenuClose}
						/>
					</>
				) : null}
			</div>
		);
	}
);

Button.defaultProps = {
	type: "button"
};
